import type { SelectedFilterOption } from 'features/charts-v2/types'
import type { FilterRestriction } from 'features/charts-v2/types/filter-restriction'
import { FilterTypes } from 'features/filters/types'
import type {
  FilterOption,
  FilterOptionSelect,
  FilterOptionValue,
} from 'features/filters/types'

/**
 * Narrows one SELECT option's values to the `allowed` set, keeping any value
 * that is currently `selected` so the user can still uncheck it. Returns the
 * SAME option reference when nothing is dropped. Shared by
 * `restrictFilterOptions` (the upstream choke point) and `FilterCard` (which
 * enforces the same subset on its own popover rather than trusting every
 * caller to have narrowed `filter` first).
 */
export function restrictSelectValues(
  option: FilterOptionSelect,
  allowed: ReadonlySet<string>,
  selected: ReadonlySet<string>,
): FilterOptionSelect {
  // A theme or dataset selection holds ids, so "selected" matches the same
  // keys "allowed" does, not the display name alone.
  const matches = (v: FilterOptionValue, keys: ReadonlySet<string>): boolean =>
    keys.has(v.value) ||
    (v.cluster_uuid !== undefined && keys.has(v.cluster_uuid)) ||
    // `identifiers` is typed as required, but real project data can
    // omit it — mirror the V1 precedent (SelectorBody.tsx) and guard.
    (v.identifiers?.some((id) => keys.has(id)) ?? false)
  const values = option.values.filter(
    (v) => matches(v, allowed) || matches(v, selected),
  )
  return values.length === option.values.length ? option : { ...option, values }
}

/**
 * Narrows a project's filter options down to the values a dashboard's global
 * filters allow, so a tile's picker can only ever narrow the dashboard —
 * never broaden past it. This is the SINGLE choke point: every internal seam
 * of `FilterSelectionPopover.tsx` (per-field list, the flattened "All
 * Properties" list, select-all, the my-brands/competitor split, the applied-
 * card `FilterPopover.tsx`) is a pure derivation of the `FilterOption[]`
 * array this function narrows, so narrowing it once upstream (in
 * `FilterControl.tsx`) covers all of them with no further edits.
 *
 * Returns the SAME array reference when `restriction` is undefined — the
 * `/charts` / Feedback / Keywords no-op path — so the `useMemo` barrier in
 * `FilterControl.tsx` holds and those callers pay nothing for this feature.
 *
 * A value that is currently SELECTED but no longer allowed is kept visible
 * (never silently dropped) so the user can uncheck it — mirrors the legacy
 * V1 panel's `SelectorBody.tsx` restriction, which keeps a currently-applied
 * out-of-range value in the list for the same reason. No write-path guard is
 * needed elsewhere: every toggle handler only ever acts on a value that is
 * visible in this narrowed list, so a disallowed value can be removed but
 * never re-added.
 */
export function restrictFilterOptions(
  options: FilterOption[],
  restriction: FilterRestriction | undefined,
  getFilterSelection: (field: string) => SelectedFilterOption | null,
): FilterOption[] {
  if (!restriction) return options

  let changed = false
  const next = options.map((option) => {
    const fieldRestriction = restriction.byField.get(option.field)
    if (!fieldRestriction) return option

    if (
      option.filter_type === FilterTypes.SELECT &&
      fieldRestriction.kind === 'values'
    ) {
      const narrowed = restrictSelectValues(
        option,
        fieldRestriction.allowed,
        new Set(getFilterSelection(option.field)?.values ?? []),
      )
      if (narrowed !== option) changed = true
      return narrowed
    }

    if (
      option.filter_type === FilterTypes.WINDOW &&
      fieldRestriction.kind === 'range'
    ) {
      const clampMin = (value: number): number =>
        fieldRestriction.min !== undefined
          ? Math.max(value, fieldRestriction.min)
          : value
      const clampMax = (value: number): number =>
        fieldRestriction.max !== undefined
          ? Math.min(value, fieldRestriction.max)
          : value

      let min = clampMin(option.min)
      let max = clampMax(option.max)
      const curMin =
        option.cur_min !== undefined ? clampMin(option.cur_min) : undefined
      const curMax =
        option.cur_max !== undefined ? clampMax(option.cur_max) : undefined

      // An inverted clamp (the dashboard's range doesn't overlap the
      // option's own bounds at all) collapses to a single point rather than
      // handing the slider a backwards range.
      if (min > max) {
        min = fieldRestriction.min ?? option.min
        max = min
      }

      if (
        min === option.min &&
        max === option.max &&
        curMin === option.cur_min &&
        curMax === option.cur_max
      ) {
        return option
      }
      changed = true
      return { ...option, min, max, cur_min: curMin, cur_max: curMax }
    }

    // DATE, or a restriction kind that doesn't match this option's
    // filter_type — untouched.
    return option
  })

  return changed ? next : options
}
